import React, { useState } from 'react';

// Drag-to-reorder for a document's line grid (Quote and Sales Order lines,
// issue #304). Same ☰-handle arming as ProductCategoryTable.js: a row only
// becomes draggable while its handle is held, so selecting text or typing
// in a row's own fields never starts a drag. The move only changes the
// screen's lines; the new order is kept on the next Save, like any other
// line edit.
//
// rowProps(line, style) spreads onto the <tr>; handle(line) renders the ☰
// beside the row number. Both do nothing while `enabled` is false.
export default function useLineReorder(setLines, enabled) {
  const [armedKey, setArmedKey] = useState(null);
  const [draggedKey, setDraggedKey] = useState(null);
  const [dropKey, setDropKey] = useState(null);

  const endDrag = () => {
    setArmedKey(null);
    setDraggedKey(null);
    setDropKey(null);
  };

  const dropOn = (targetKey) => {
    const fromKey = draggedKey;
    endDrag();
    if (fromKey === null || fromKey === targetKey) return;
    setLines((prev) => {
      const fromIdx = prev.findIndex((l) => l.key === fromKey);
      const toIdx = prev.findIndex((l) => l.key === targetKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const rowProps = (line, style) => {
    if (!enabled) return { style };
    return {
      draggable: armedKey === line.key,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(line.key));
        setDraggedKey(line.key);
      },
      onDragOver: (e) => {
        if (draggedKey === null || draggedKey === line.key) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropKey !== line.key) setDropKey(line.key);
      },
      onDragLeave: () => { if (dropKey === line.key) setDropKey(null); },
      onDrop: (e) => { e.preventDefault(); dropOn(line.key); },
      onDragEnd: endDrag,
      style: {
        ...style,
        opacity: draggedKey === line.key ? 0.4 : 1,
        background: dropKey === line.key ? '#e3f2fd' : style?.background,
      },
    };
  };

  const handle = (line) => (enabled ? (
    <span
      title="Drag to reorder"
      onMouseDown={() => setArmedKey(line.key)}
      onMouseUp={() => setArmedKey(null)}
      style={{ float: 'left', paddingLeft: 2, color: '#888', cursor: 'grab', userSelect: 'none' }}
    >
      ☰
    </span>
  ) : null);

  return { rowProps, handle };
}
